import { fabric } from 'fabric';
import cloudinaryCore from 'cloudinary-core';
import concat from 'lodash-es/concat';
import get from 'lodash-es/get';
import isFunction from 'lodash-es/isFunction';
import utils from './utils';
import variables from './variables';
import { isObjectOfType } from './objects';
import Color from 'color';

const cloudinary = new cloudinaryCore.Cloudinary(window.config.cloudinary);

export const getMaxImageWidth = info => {
  // Limit image dimensions
  let maxSize = variables.maxImgSize;
  // When provided more info about size (e.g. uploads),
  // we can better adjust limits for weird ratios.
  if (info && info.width && info.height) {
    if (info.width < maxSize && info.height < maxSize) {
      maxSize = info.width;
    } else if (info.height > maxSize && info.height > info.width) {
      maxSize = Math.round(info.width * (maxSize / info.height));
    }
  }
  return maxSize;
};

export const getLimitedDimensions = info => {
  const width = getMaxImageWidth(info);
  const height = Math.round((width / info.width) * info.height);
  return { width, height };
};

export const getCloudinaryVersionNumber = () => Math.ceil(Date.now() / 1000);

export const getCloudinaryURL = (url, options = {}, info = {}) => {
  const transformations = [];
  if (options.resource_type === 'video') {
    transformations.push({ quality: '65' });
  } else {
    transformations.push({ quality: 'auto:eco' });
    transformations.push({ flags: 'apng.lossy' });
    // transformations.push({ angle: 'ignore' });
    transformations.push({ crop: 'limit', width: getMaxImageWidth(info) });
  }

  return cloudinary.url(url, {
    transformation: transformations,
    ...options,
  });
};

export const getImageURLKeyFromParams = params =>
  `${params.publicId}.${params.format}`;

export const getVersionedImageURLKeyFromParams = params =>
  `v${params.version}/${getImageURLKeyFromParams(params)}`;

// Proxied images

export const getCachedImageURLFromURL = (url, options = {}) =>
  getCloudinaryURL(url, { type: 'fetch', ...options });

// Cropped image box metrics$

const getImageScale = params => {
  if (!params.ludus.limitedWidth && !params.ludus.originalWidth) {
    return 1;
  }

  const w = params.cropWidth
    ? params.width * (params.imageScaleX || 1)
    : params.width;
  const h = params.cropHeight
    ? params.height * (params.imageScaleY || 1)
    : params.height;
  const ratio = w / h;
  const cropWidth = params.imageScaleX
    ? params.cropWidth || params.width
    : params.ludus.originalWidth;

  const cropHeight = params.imageScaleY
    ? params.cropHeight || params.height
    : params.ludus.originalHeight;

  const cropRatio = cropWidth / cropHeight;

  const scale = ratio > cropRatio ? w / cropWidth : h / cropHeight;
  return scale;
};

export const getCroppedImageMetricsFromImageObject = object => {
  if (!object) {
    return null;
  }

  const params = isFunction(object.toJSON)
    ? { ...object.toJSON(variables.propsToSave) }
    : { ...object };

  if (params.ludus?.ludusImageVersion !== '4.0' && params.cropWidth) {
    params.cropWidth = params.cropWidth * (params.imageScaleX || 1);
    params.cropHeight = params.cropHeight * (params.imageScaleY || 1);
  }
  // mitigate risk of images being flagged as 4.0 but that has not been manually cropped
  // A 4.0 image can't have a crop with no image scale applied
  if (params.imageScaleX && (!params.cropWidth || !params.cropHeight)) {
    params.cropWidth = params.width;
    params.cropHeight = params.height;
  }

  const imageScaleX = getImageScale(params);
  const imageScaleY = imageScaleX;

  // Actual object dimensions on Fabric canvas, unscaled
  const objectWidth = params.width / imageScaleX;
  const objectHeight = params.height / imageScaleY;
  // Object scale
  const scaleX = params.scaleX || 1;
  const scaleY = params.scaleY || 1;
  // Object scale, capped at 100% maximumlimitedHeight
  const cappedScaledWidth = objectWidth * Math.min(1, scaleX);
  const cappedScaledHeight = objectHeight * Math.min(1, scaleY);
  // Original file dimensions
  const originalWidth =
    get(params, 'ludus.originalWidth') ||
    get(params, 'ludus.limitedWidth') ||
    objectWidth;
  const originalHeight =
    get(params, 'ludus.originalHeight') ||
    get(params, 'ludus.limitedHeight') ||
    objectHeight;
  // Limited file dimensions suitable for the editor
  const limitedWidth = get(params, 'ludus.limitedWidth') || originalWidth;
  const limitedHeight = get(params, 'ludus.limitedHeight') || originalHeight;
  // Ratio between the dimensions of the actual file in the editor and the original upload
  const limitedWidthRatio = (params.width * params.scaleX) / limitedWidth; // originalWidth;
  const limitedHeightRatio = (params.height * params.scaleY) / limitedHeight; // originalHeight;
  // Crop coordinates, adjusted to original file dimensions
  // in the case the file dimensions had to be limited

  const cropX = params.imageCropX || 0;
  const cropY = params.imageCropY || 0;
  const positiveCropX = Math.max(0, cropX);
  const positiveCropY = Math.max(0, cropY);
  // Top/left padding in the case someone leaves additional space around the picture (negative crop)
  const negativeCropX = positiveCropX - cropX;
  const negativeCropY = positiveCropY - cropY;
  // Scaled padding, adjusted to canvas coordinates
  const scaledNegativeCropX = negativeCropX * limitedWidthRatio * scaleX;
  const scaledNegativeCropY = negativeCropY * limitedHeightRatio * scaleY;
  // Negative crop is only used by v2 crop not v3 and higher (we fill the crop area with the pic)
  // Crop dimensions, adjusted to original file dimensions.
  // We take the smallest value between either:
  // - the actual unscaled object dimensions on canvas adjusted to
  //   original file dimensions, minus additional padding
  // - the original file dimensions, minus crop value

  // objectWidth/height : real size of the object non-scaled
  const cropWidth = objectWidth;
  const cropHeight = objectHeight;
  // Scaled crop dimensions, adjusted to canvas coordinates. Actual image dimensions on canvas.
  const scaledCropWidth = params.width * (scaleX || 1);
  const scaledCropHeight = params.height * (scaleY || 1);

  return {
    cropX:
      parseFloat(params.ludus.ludusImageVersion) < 3.0
        ? positiveCropX
        : Math.max(
            0,
            limitedWidth / 2 -
              (params.width / imageScaleX / 2 + (params.imageCropX || 0)),
          ),
    cropY:
      parseFloat(params.ludus.ludusImageVersion) < 3.0
        ? positiveCropY
        : Math.max(
            0,
            // params.ludus.originalHeight / 2   -params.height/2 // CENTER
            limitedHeight / 2 -
              (params.height / imageScaleY / 2 + (params.imageCropY || 0)),
          ),
    cropWidth,
    cropHeight,
    cappedScaledWidth,
    cappedScaledHeight,
    paddingLeft: scaledNegativeCropX * imageScaleX,
    paddingTop: scaledNegativeCropY * imageScaleY,
    width: Math.round(scaledCropWidth),
    height: Math.round(scaledCropHeight),
  };
};

// Viewer images
// Also called from editor to pre-heat cache

export const getCachedImageURLFromImageObject = (object, opts) => {
  const options = {
    cache: 'auto',
    autoFetchFormat: false,
    forcedVersion: null,
    ...opts,
  };
  const params = isFunction(object.toJSON)
    ? object.toJSON(variables.propsToSave)
    : object;
  if (!params.width || !params.height) {
    // bug from import PDF not storing width/height for some reasons?
    params.width = get(params, 'ludus.originalWidth') || 500;
    params.height = get(params, 'ludus.originalHeight') || 500;
  }
  const cropMetrics = getCroppedImageMetricsFromImageObject(params);

  let url;
  const config = {};
  const devicePixelRatio = window.devicePixelRatio || 1;
  const autoCacheSources = ['dropbox'];
  const transformations = [
    { quality: params.ludus.highQuality ? 'auto:best' : 'auto:eco' },
    {
      dpr: params.ludus.highQuality
        ? Math.max(devicePixelRatio, 3.0)
        : devicePixelRatio,
    },
  ];

  // Crop images only if Fabric version >= 2.0 (version was not specified before)
  const isCropEnabled = typeof params.version !== 'undefined';
  const cappedScaledWidth = Math.round(cropMetrics.cappedScaledWidth);
  const cappedScaledHeight = Math.round(cropMetrics.cappedScaledHeight);

  if (object.ludus.format !== 'svg') {
    transformations.push({
      crop: 'limit',
      width:
        get(object, 'ludus.limitedWidth') ||
        get(object, 'ludus.originalWidth') ||
        params.width,
      height:
        get(object, 'ludus.limitedHeight') ||
        get(object, 'ludus.originalHeight') ||
        params.height,
    });

    // }
    if (isCropEnabled) {
      transformations.push({
        crop: 'crop',
        x: Math.round(cropMetrics.cropX) || 0,
        y: Math.round(cropMetrics.cropY) || 0,
        width: Math.round(cropMetrics.cropWidth),
        height: Math.round(cropMetrics.cropHeight),
      });

      transformations.push({
        crop: 'limit',
        width: cropMetrics.width || cappedScaledWidth,
        height: cropMetrics.height || cappedScaledHeight,
      });
    }
  } else {
    transformations.push({
      crop: 'scale',
      width: Math.round(params.width * params.scaleX),
      height: Math.round(params.height * params.scaleY),
      fetch_format: object.tint ? 'png' : 'svg',
    });
  }

  const p = { ...params.ludus };
  if (options.forcedVersion) {
    p.version = options.forcedVersion;
  }

  if (p.publicId && p.format && p.version) {
    url = getVersionedImageURLKeyFromParams(p);
  } else if (params.ludus.originalUrl) {
    url = params.ludus.originalUrl;
    if (
      options.cacheKey &&
      (options.cache === 'all' ||
        options.cache === true ||
        (options.cache === 'auto' &&
          autoCacheSources.includes(params.ludus.source)))
    ) {
      url += `?${options.cacheKey}`;
    }
    config.type = 'fetch';
  }

  if (params.ludus.source === 'giphy') {
    if (
      utils.isSafari &&
      window.navigator.userAgent.indexOf('Version/12') > 0
    ) {
      // Safari 12 can play gif in <img> tag
      transformations.push({ fetch_format: 'mp4' });
    } else {
      transformations.push({ fetch_format: 'auto' });
    }
  }

  if (params.ludus.format === 'png') {
    transformations.push({ flags: 'apng.lossy' });
  } else if (!params.ludus.highQuality) {
    transformations.push({ flags: 'lossy' });
  }

  if (options.autoFetchFormat) {
    transformations.push({ fetch_format: 'auto' });
  }

  // transformations.push({ angle: 'ignore' });

  if (params.tint) {
    const color = Color(params.tint).hex();
    const alpha = Math.round(Color(params.tint).alpha() * 100);
    transformations.push({ effect: `colorize:${alpha}`, color });
  }
  if (params.blur) {
    const largestSide =
      cappedScaledWidth > cappedScaledHeight
        ? cappedScaledWidth
        : cappedScaledHeight;
    transformations.push({
      effect: `blur:${Math.round(params.blur * 4 * largestSide)}`,
    });
  }
  return getCloudinaryURL(url, {
    ...config,
    transformation: transformations,
  });
};

export const loadImageWithParams = (params, cachedImageData) =>
  new Promise((resolve, reject) => {
    let finalUrl;

    const {
      publicId,
      originalWidth,
      originalHeight,
      type,
      format,
      url,
    } = params;
    if ((!cachedImageData || !cachedImageData.failed) && publicId) {
      // Either a local file URL or a working Cloudinary URL
      finalUrl = cachedImageData ? cachedImageData.url : url;
    } else if (
      cachedImageData &&
      cachedImageData.failed &&
      publicId &&
      format
    ) {
      // Try with the current timestamp as the version parameter
      const version = getCloudinaryVersionNumber();
      finalUrl = getCloudinaryURL(
        getVersionedImageURLKeyFromParams({
          publicId,
          format,
          version,
        }),
        { resource_type: type },
        { width: originalWidth, height: originalHeight },
      );
    } else {
      finalUrl = url;
    }

    if (!finalUrl) {
      return false;
    }
    const isGif =
      finalUrl
        .split('.')
        .pop()
        .toLowerCase() === 'gif';
    // make sure we don't load a complete gif in fabric (showing a static image anyway).
    // fix a bug in safari animating the gif while canvas is rendered
    if (isGif) {
      finalUrl = finalUrl.replace('upload/', 'upload/pg_1/');
    }
    fabric.util.loadImage(
      finalUrl,
      image => {
        if (image) {
          resolve({
            image,
            url: finalUrl,
            wasFailedBefore: cachedImageData && cachedImageData.failed,
            wasLoadedBefore: cachedImageData && cachedImageData.loaded,
            wasLocalBefore: cachedImageData && cachedImageData.local,
          });
        } else {
          reject(null);
        }
      },
      null,
      'Anonymous',
    );
  });

// Viewer videos

export const getCachedVideoURL = ({
  publicId,
  format,
  startTime,
  endTime,
  highQuality,
  forcedVersion,
  height,
  width,
  params,
}) => {
  let urlKey;
  if (forcedVersion) {
    urlKey = getVersionedImageURLKeyFromParams({
      publicId,
      format,
      version: forcedVersion,
    });
  } else {
    urlKey = getImageURLKeyFromParams({ publicId, format });
  }

  const cropMetrics = getCroppedImageMetricsFromImageObject(params);

  const transformations = [
    { quality: highQuality ? '85' : '65' },
    height &&
      width && {
        crop: 'crop',
        x: Math.round(cropMetrics.cropX),
        y: Math.round(cropMetrics.cropY),
        width: Math.round(cropMetrics.cropWidth),
        height: Math.round(cropMetrics.cropHeight),
      },
  ].filter(Boolean);

  transformations.push({
    crop: 'limit',
    width: cropMetrics
      ? cropMetrics.width || cropMetrics.cappedScaledWidth
      : width,
    height: cropMetrics
      ? cropMetrics.height || cropMetrics.cappedScaledHeight
      : height,
  });

  const timeConfig = {};
  timeConfig.start_offset = `${startTime || 0}`;
  if (endTime) timeConfig.end_offset = `${endTime}`;
  transformations.push(timeConfig);

  return getCloudinaryURL(urlKey, {
    resource_type: 'video',
    transformation: transformations,
  });
};

export const getCachedVideoURLFromVideoObject = (
  object,
  { format, forcedVersion },
) => {
  const { source, url, startTime = 0, highQuality = false } = object.ludus;
  if (source !== 'Cloudinary') {
    return object.ludus.thumbnail;
  }

  let { publicId, endTime } = object.ludus;
  if ((endTime || 0) <= startTime) {
    endTime = null;
  }
  if (!publicId) {
    publicId = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));
  }
  return getCachedVideoURL({
    publicId,
    format,
    startTime,
    endTime,
    highQuality,
    hflip: object.flipX,
    vflip: object.flipY,
    forcedVersion,
    width: object.width,
    height: object.height,
    params: object,
  });
};

export const getSafeImageURLFromParams = (state, publicId, url) => {
  const imageState = state.imagesReducer.images[publicId];

  if (imageState && imageState.url) {
    return imageState.url;
  }
  return url;
};

// Image colors

const defaultRGB = { r: 128, g: 128, b: 128 };

export const getAverageRGBFromImageContext = (context, speed = 8) => {
  const rgb = {
    r: 0,
    g: 0,
    b: 0,
    hasAlpha: 0,
  };
  let count = 0;

  let imageData;
  try {
    imageData = context.getImageData(
      0,
      0,
      context.canvas.width,
      context.canvas.height,
    );
  } catch (e) {
    // security error, img on diff domain
    return defaultRGB;
  }

  const length = imageData.data.length;
  let hasAlpha = false;
  for (let i = 0; i < length; i += speed * 4) {
    rgb.r += imageData.data[i];
    rgb.g += imageData.data[i + 1];
    rgb.b += imageData.data[i + 2];
    if (!hasAlpha) hasAlpha = imageData.data[i + 3] === 0;
    count += 1;
  }

  rgb.r = Math.floor(rgb.r / count);
  rgb.g = Math.floor(rgb.g / count);
  rgb.b = Math.floor(rgb.b / count);
  rgb.hasAlpha = hasAlpha;
  return rgb;
};

export const getAverageRGBFromImage = (image, speed) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext && canvas.getContext('2d');

  if (!context) {
    return defaultRGB;
  }

  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  canvas.width = width;
  canvas.height = height;

  context.drawImage(image, 0, 0);

  return getAverageRGBFromImageContext(context, speed);
};

export const getColorsFromImageOrContext = (image, context) => {
  if (image) {
    const colorObject = getAverageRGBFromImage(image);
    const main = utils.getRGBStringFromRGBObject(colorObject);
    const hasAlpha = colorObject.hasAlpha;
    const contrast = utils.getContrastColor(main);
    return { main, contrast, hasAlpha };
  }
  if (context) {
    const main = utils.getRGBStringFromRGBObject(
      getAverageRGBFromImageContext(context),
    );
    const contrast = utils.getContrastColor(main);
    return { main, contrast };
  }
  return null;
};

export const findImagesInObjectsByParam = (objects, paramName, paramValue) =>
  objects
    ? Object.values(objects).reduce((imageObjects, currentObject) => {
        if (
          isObjectOfType(currentObject, 'image', 'video') &&
          get(currentObject, paramName) === paramValue
        ) {
          return concat(imageObjects, currentObject);
        }
        if (
          isObjectOfType(currentObject, 'group', 'symbol', 'activeSelection')
        ) {
          return concat(
            imageObjects,
            findImagesInObjectsByParam(
              currentObject.objects,
              paramName,
              paramValue,
            ),
          );
        }
        return imageObjects;
      }, [])
    : [];

export const findImagesInObjectsByPublicId = (objects, publicId) =>
  findImagesInObjectsByParam(objects, 'ludus.publicId', publicId);

export const getChangesForOverrideImageData = (imageData, overrides) => {
  const minScaleX = imageData.width / overrides.ludus.limitedWidth;
  const minScaleY = imageData.height / overrides.ludus.limitedHeight;
  const scale = Math.max(minScaleX, minScaleY);
  return {
    cropWidth: overrides.ludus.limitedWidth,
    cropHeight: overrides.ludus.limitedHeight,
    imageCropX: 0,
    imageCropY: 0,
    imageScaleX: scale,
    imageScaleY: scale,
  };
};

export const overrideImageData = (imageData, overrides, shouldUpdateFlag) => {
  const changes = getChangesForOverrideImageData(imageData, overrides);
  return {
    ...imageData,
    ...overrides,
    ...changes,
    ludus: {
      ...imageData.ludus,
      ...overrides.ludus,
      overriden: shouldUpdateFlag,
    },
  };
};
